import React, { useId, useState } from 'react';
import { Box, Chip, Collapse, IconButton, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { useTranslation } from 'react-i18next';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import { ESG_ICONS } from '../../constants/esgIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { EsgBranchMonthlyRow } from '../../types/esg-dashboard.type';
import type { EsgBranchesWidgetId } from '../../types/dashboard-customize.type';
import { UtilityCategory } from '../../types/utility-emission.type';
import { carbonMaterialLabel } from '../../utils/carbonAvoidance.util';
import { headingCase } from '../../utils/titleCase';
import { contentBorders } from '../../theme/createContentTheme';
import KpiFigure from '../metrics/KpiFigure';

type BranchSiteCardProps = {
  row: EsgBranchMonthlyRow;
  orgTrainingHours: number | null | undefined;
  formatSar: (amount: number) => string;
  visibleWidgets?: readonly EsgBranchesWidgetId[];
  /** Mark full card for multi-hole coachmark spotlight. */
  highlightHole?: boolean;
  /** Controlled expansion (e.g. "Expand all"); uncontrolled when omitted. */
  expanded?: boolean;
  onToggle?: () => void;
};

const utilityIcon: Record<UtilityCategory, SvgIconComponent> = {
  [UtilityCategory.WATER]: ESG_ICONS.water,
  [UtilityCategory.ELECTRICITY]: ESG_ICONS.electricity,
  [UtilityCategory.GAS]: ESG_ICONS.gas,
  [UtilityCategory.OIL]: ESG_ICONS.oil,
  [UtilityCategory.OTHER]: ESG_ICONS.materials,
};

/** Width of the expand-button column (kept even when a row has no details, for alignment). */
const TOGGLE_COLUMN = '40px';

/** One labelled detail group (a grid cell); hairlines on its top and inline-start edges. */
const Group: React.FC<{ icon: SvgIconComponent; label: string; children: React.ReactNode }> = ({
  icon: Icon,
  label,
  children,
}) => {
  const { i18n } = useTranslation();
  return (
    <Box
      sx={(theme) => ({
        minWidth: 0,
        px: { xs: 2, md: 2.5 },
        py: 2,
        borderTop: `1px solid ${contentBorders(theme).subtle}`,
        borderInlineStart: `1px solid ${contentBorders(theme).subtle}`,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
        <Icon aria-hidden sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="overline" component="h4" sx={{ m: 0, color: 'text.secondary', lineHeight: 1.4 }}>
          {headingCase(label, i18n.language)}
        </Typography>
      </Box>
      {/* Auto-height wrapper: KpiFigure is height:100%, which would otherwise fill the stretched cell. */}
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
};

/**
 * Collection-site row on the Branches tab. Collapsed: site name and the headline
 * figures (waste collected · avoided emissions · resource use) in columns that
 * line up across every branch. Expanded: fill reports, avoided emissions by
 * material, GHG inventory, the utility breakdown and training hours.
 * Columns depend only on the selected widgets, never on a row's data, so all
 * rows share one grid.
 */
const BranchSiteCard: React.FC<BranchSiteCardProps> = ({
  row,
  orgTrainingHours,
  formatSar,
  visibleWidgets,
  highlightHole = false,
  expanded: expandedProp,
  onToggle,
}) => {
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const detailsId = useId();
  const [expandedLocal, setExpandedLocal] = useState(false);
  const expanded = expandedProp ?? expandedLocal;
  const toggle = () => (onToggle ? onToggle() : setExpandedLocal((v) => !v));

  const siteId = row.collection_site_id ?? '';
  const unassigned = siteId === '__unassigned__' || siteId.toLowerCase().includes('unassigned');
  const siteName = unassigned
    ? t('dashboard.branch.compare.unassignedBranch')
    : (row.collection_site_name || siteId).trim() || t('dashboard.branch.compare.unassignedBranch');
  const show = (id: EsgBranchesWidgetId) => !visibleWidgets || visibleWidgets.includes(id);

  const lines = row.scope_emissions?.activity_lines ?? [];
  const streams = row.avoided_tco2e_by_stream ?? [];
  const utilityCost = lines.reduce((sum, line) => sum + (line.amount_sar ?? 0), 0);

  /* ---------------------------------------------------------------- summary */
  const waste = fmt.metric('mass_kg', row.total_waste_diverted_kg, { scaleMass: true });
  const avoided = fmt.metric('tco2e', row.total_avoided_tco2e ?? 0);
  const cost = fmt.metric('sar', utilityCost);
  const summary = [
    show('total_waste') && (
      <KpiFigure
        key="waste"
        label={t('dashboard.branch.totalWaste')}
        icon={ESG_ICONS.wasteCollected}
        value={waste.text}
        unit={waste.unit}
        size="sm"
      />
    ),
    show('carbon_avoided') && (
      <KpiFigure
        key="avoided"
        label={t('dashboard.branch.carbonAvoided')}
        icon={ESG_ICONS.avoidedEmissions}
        value={avoided.text}
        unit={avoided.unit}
        size="sm"
      />
    ),
    show('utilities') && (
      <KpiFigure
        key="resources"
        label={t('dashboard.sections.resourceUse')}
        icon={ESG_ICONS.electricity}
        value={lines.length ? cost.text : '—'}
        unit={lines.length ? cost.unit : undefined}
        context={lines.length ? t('dashboard.branch.utilityCost') : t('common.noData')}
        size="sm"
      />
    ),
  ].filter(Boolean) as React.ReactElement[];
  const metricCount = summary.length;
  const wideColumns = ['minmax(0, 1.4fr)', ...summary.map(() => 'minmax(0, 1fr)'), TOGGLE_COLUMN].join(' ');

  /* ---------------------------------------------------------------- details */
  const scopeFigure = (n: 1 | 2, value: number | null) => {
    const f = fmt.metric('tco2e', value);
    return (
      <KpiFigure
        label={t('dashboard.inventory.scopeN', { n })}
        value={f.text}
        unit={value === null ? undefined : f.unit}
        size="sm"
        context={value === null ? t('dashboard.inventory.incomplete') : undefined}
      />
    );
  };

  const details = [
    show('fill_reports') && (
      <Group key="fill" icon={ESG_ICONS.wasteCollected} label={t('dashboard.sections.wasteDiversion')}>
        <KpiFigure
          label={t('dashboard.branch.fillReports')}
          value={fmt.metric('count', row.fill_level_report_count).text}
          unit={t('dashboard.branch.reportsLabel')}
          size="sm"
        />
      </Group>
    ),
    show('carbon_by_stream') && streams.length > 0 && (
      <Group key="streams" icon={ESG_ICONS.avoidedEmissions} label={t('carbonAvoidance.byStreamTitle')}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {streams.map((stream) => (
            <Chip
              key={stream.material_label}
              size="small"
              variant="outlined"
              label={
                <>
                  {carbonMaterialLabel(stream.material_label, t('carbonAvoidance.unclassified'))} ·{' '}
                  <bdi>{fmt.metric('tco2e', stream.total_avoided_tco2e).text}</bdi> {t('units.tco2e')}
                </>
              }
            />
          ))}
        </Box>
        <Typography variant="caption" component="p" sx={{ mt: 1, color: 'text.secondary' }}>
          {t('dashboard.basedOnDeliveryDate')}
        </Typography>
      </Group>
    ),
    (show('scope_1') || show('scope_2')) && (
      <Group key="inventory" icon={ESG_ICONS.ghgInventory} label={t('dashboard.sections.ghgInventory')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 2, rowGap: 1.5 }}>
          {show('scope_1') && scopeFigure(1, row.scope_emissions?.scope_1_tco2e ?? null)}
          {show('scope_2') && scopeFigure(2, row.scope_emissions?.scope_2_tco2e ?? null)}
        </Box>
      </Group>
    ),
    show('utilities') && (
      <Group key="utilities" icon={ESG_ICONS.electricity} label={t('dashboard.sections.resourceUse')}>
        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.branch.noUtilityData')}
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {lines.map((line) => {
              const Icon = utilityIcon[line.category] ?? ESG_ICONS.materials;
              return (
                <Box
                  component="li"
                  key={line.utility_type_key}
                  sx={(theme) => ({
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'baseline',
                    columnGap: 2,
                    py: 0.75,
                    borderBottom: `1px solid ${contentBorders(theme).subtle}`,
                    '&:last-of-type': { borderBottom: 0 },
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Icon aria-hidden sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography variant="body2" noWrap title={line.label}>
                      {line.label}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'end' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      <bdi>{fmt.number(line.consumption)}</bdi>{' '}
                      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {line.canonical_unit}
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <bdi>{formatSar(line.amount_sar)}</bdi>
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Group>
    ),
    show('training_hours') && (
      <Group key="training" icon={ESG_ICONS.training} label={t('dashboard.branch.trainingHours')}>
        <KpiFigure
          label={t('dashboard.context.organizationWide')}
          value={typeof orgTrainingHours === 'number' ? fmt.metric('hours', orgTrainingHours).text : '—'}
          unit={typeof orgTrainingHours === 'number' ? t('units.hours') : undefined}
          size="sm"
        />
      </Group>
    ),
    show('health_safety') && row.hse && row.hse.weeks_reported > 0 && (
      <Group key="hse" icon={ESG_ICONS.healthSafety} label={t('dashboard.sections.healthSafety')}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          <KpiFigure
            label={t('hse.dashboard.recordableInjuries')}
            value={fmt.metric('count', row.hse.recordable_injuries).text}
            size="sm"
          />
          <KpiFigure label={t('hse.dashboard.lostDays')} value={fmt.metric('count', row.hse.lost_days).text} size="sm" />
          <KpiFigure
            label={t('hse.dashboard.totalIncidents')}
            value={fmt.metric('count', row.hse.total_incidents).text}
            size="sm"
          />
          <KpiFigure
            label={t('hse.dashboard.trir')}
            value={row.hse.trir === null ? '—' : fmt.number(row.hse.trir, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            unit={row.hse.trir === null ? undefined : t('hse.dashboard.per200k')}
            context={t('hse.dashboard.weeksSubmitted', {
              submitted: fmt.number(row.hse.weeks_submitted),
              reported: fmt.number(row.hse.weeks_reported),
            })}
            size="sm"
          />
        </Box>
      </Group>
    ),
  ].filter(Boolean) as React.ReactElement[];
  const hasDetails = details.length > 0;

  return (
    <Paper
      variant="outlined"
      {...(highlightHole ? { 'data-setup-hole': true } : {})}
      sx={{ borderRadius: `${ESG_CONTENT_SHAPE.md}px`, bgcolor: 'background.paper', overflow: 'hidden' }}
    >
      {/* Summary row — click anywhere (or the chevron) to expand */}
      <Box
        onClick={hasDetails ? toggle : undefined}
        sx={(theme) => ({
          display: 'grid',
          alignItems: 'center',
          columnGap: { xs: 2, md: 3 },
          rowGap: 1.5,
          px: { xs: 2, md: 2.5 },
          py: 2,
          gridTemplateColumns: {
            xs: `repeat(${Math.max(metricCount, 1)}, minmax(0, 1fr)) ${TOGGLE_COLUMN}`,
            md: wideColumns,
          },
          cursor: hasDetails ? 'pointer' : 'default',
          transition: 'background-color 150ms ease',
          bgcolor: expanded ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
          '&:hover': hasDetails ? { bgcolor: alpha(theme.palette.primary.main, 0.04) } : undefined,
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
            gridColumn: { xs: `1 / ${Math.max(metricCount, 1) + 1}`, md: 'auto' },
            gridRow: { xs: 1, md: 'auto' },
          }}
        >
          <Box
            aria-hidden
            sx={(theme) => ({
              width: 36,
              height: 36,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: `${ESG_CONTENT_SHAPE.sm}px`,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            })}
          >
            <ESG_ICONS.site sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, m: 0, minWidth: 0 }} noWrap title={siteName}>
            {siteName}
          </Typography>
        </Box>

        {summary.map((cell) => (
          <Box key={cell.key} sx={{ minWidth: 0, alignSelf: 'start', gridRow: { xs: 2, md: 'auto' } }}>
            {cell}
          </Box>
        ))}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gridColumn: { xs: Math.max(metricCount, 1) + 1, md: 'auto' },
            gridRow: { xs: 1, md: 'auto' },
          }}
        >
          {hasDetails ? (
            <IconButton
              size="small"
              aria-expanded={expanded}
              aria-controls={detailsId}
              aria-label={`${expanded ? t('dashboard.branch.hideDetails') : t('dashboard.branch.showDetails')} · ${siteName}`}
              sx={{ color: 'text.secondary' }}
            >
              <ExpandMoreRounded
                sx={{ transition: 'transform 200ms ease', transform: expanded ? 'rotate(180deg)' : 'none' }}
              />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      {/* Details */}
      {hasDetails ? (
        <Collapse in={expanded} timeout={220} unmountOnExit>
          <Box
            id={detailsId}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: `repeat(${Math.min(details.length, 4)}, minmax(0, 1fr))`,
                xl: `repeat(${details.length}, minmax(0, 1fr))`,
              },
              alignItems: 'stretch',
              // Hide the outermost inline-start hairline under the panel edge.
              marginInlineStart: '-1px',
            }}
          >
            {details}
          </Box>
        </Collapse>
      ) : null}
    </Paper>
  );
};

export default BranchSiteCard;
